/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { Router } from '@angular/router'
import { PidxLoaderModeEnum } from '@myprofile-ui/pidx-ui-components'
import { PidxI18nService, WINDOW } from '@myprofile-ui/pidx-utils'
import { Subject, lastValueFrom, of } from 'rxjs'
import { catchError, takeUntil } from 'rxjs/operators'
import { MyProfilePreferredLanguageWidgetTranslations } from './interfaces/myprofile-preferred-language-widget.interfaces'
import { RESTRICT_GLOBAL_UI_CHANGES } from '../../../pidx-utils/src/lib/helpers/restrictGlobalUIChanges.injectionToken'
import { ProfileService, PublicProfile } from '../../../../apps/myprofile-ui/src/app/services/profile/profile.service'

@Component({
  selector: 'myprofile-preferred-language-widget',
  templateUrl: './myprofile-preferred-language-widget.component.html',
  host: { class: 'profile:block profile:relative profile:break-words' },
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfilePreferredLanguageWidgetComponent implements OnInit, OnDestroy {
  protected window = inject<Window>(WINDOW)
  private i18nService = inject(PidxI18nService)
  private profileService = inject(ProfileService)
  private router = inject(Router)
  private changeDetectorRef = inject(ChangeDetectorRef)

  private unsubscribe = new Subject<void>()

  @Input() translations!: MyProfilePreferredLanguageWidgetTranslations
  @Input() country!: string
  @Input() locale!: string
  @Input() appBaseUrl!: string

  public isLoading = true
  public isRefreshing = false

  public currentPreferredLanguage = ''
  public currentPreferredLanguageTitle = ''
  public currentLanguageHtmlLang = ''
  public newPreferredLanguage = ''
  public dropdownItems: { value: string; title: string }[] = []
  public editMode = false
  public languageDropdownFormGroup!: UntypedFormGroup
  public profile!: PublicProfile
  public restrictGlobalUIChanges = inject<boolean>(RESTRICT_GLOBAL_UI_CHANGES)

  public async ngOnInit() {
    await this.fetchProfile()
  }

  public ngOnDestroy() {
    this.unsubscribe.next()
    this.unsubscribe.complete()
  }

  public async fetchProfile() {
    this.profile = await lastValueFrom(this.profileService.getProfile(this.country, this.locale).pipe(catchError((error) => of(error))))

    this.isLoading = false
    this.getAvailableLanguages()
    this.changeDetectorRef.markForCheck()
  }

  public getAvailableLanguages() {
    this.dropdownItems = this.i18nService
      .getLanguageChooserLinks((this.profile.preferredCountry ?? '').toLowerCase(), this.locale.split('_')[0])
      .map((item) => {
        const [value, title] = Object.entries(item)[0]
        return { value, title }
      })
    this.newPreferredLanguage = this.currentPreferredLanguage = this.reformatPreferredLanguage()
    const foundLanguage = this.dropdownItems.find((language) => language.value === this.currentPreferredLanguage)
    this.currentPreferredLanguageTitle = foundLanguage?.title || ''
    this.currentLanguageHtmlLang = this.toBcp47(this.currentPreferredLanguage)

    this.languageDropdownFormGroup = new UntypedFormGroup({
      languageDropdown: new UntypedFormControl(this.currentPreferredLanguage),
    })

    this.languageDropdownFormGroup
      .get('languageDropdown')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe))
      .subscribe((eventData: string) => (this.newPreferredLanguage = eventData))
  }

  public reformatPreferredLanguage() {
    const language = this.profile.language ?? ''
    return language.split('-')[0].toLowerCase() + '_' + (language.split('-')[1] ?? '').toUpperCase()
  }

  public getPreferredLanguagePayload() {
    return this.newPreferredLanguage.split('_')[0].toLowerCase() + '-' + this.newPreferredLanguage.split('_')[1].toLowerCase()
  }

  public async savePreferredLanguage() {
    this.isRefreshing = true
    await lastValueFrom(
      this.profileService
        .updateProfile(this.country, this.locale, {
          ...this.profile,
          preferredLanguage: this.getPreferredLanguagePayload(),
        })
        .pipe(catchError((error) => of(error))),
    )

    this.isRefreshing = false
    this.reloadWithNewLanguage()
  }

  public reloadWithNewLanguage() {
    // This will get the url-path after the country/locales like 'success-page'
    const routeSegmentsWithoutCountryAndLocale = this.router.url.split('/').slice(3)

    this.window.location.assign(
      [
        this.appBaseUrl,
        (this.profile.preferredCountry ?? '').toLowerCase(),
        this.newPreferredLanguage,
        ...routeSegmentsWithoutCountryAndLocale,
      ].join('/'),
    )
  }

  public enterEditMode() {
    this.editMode = true
    this.window.setTimeout(() => {
      this.window.document.getElementById('preferred-language-select')?.focus()
    }, 0)
  }

  public leaveEditMode() {
    this.currentPreferredLanguage = this.reformatPreferredLanguage()
    this.newPreferredLanguage = this.currentPreferredLanguage
    this.languageDropdownFormGroup.get('languageDropdown')?.setValue(this.currentPreferredLanguage)
    this.editMode = false
    this.window.setTimeout(() => {
      this.window.document.getElementById('preferred-language-edit-btn')?.focus()
    }, 0)
  }

  get isRestricted(): boolean {
    return ['CN'].includes(this.profile?.preferredCountry ?? '') && this.restrictGlobalUIChanges
  }

  /** Converts the internal `xx_YY` language value to a BCP-47 tag for the `lang` attribute. */
  public toBcp47(value: string): string {
    if (!value) {
      return ''
    }
    const [lang, region] = value.split('_')
    return region ? `${lang}-${region}` : lang
  }

  protected readonly PidxLoaderModeEnum = PidxLoaderModeEnum
}
